import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { Folder, File, Trash, Download, Lock } from "lucide-react";
import { useTheme } from "../ThemeContext";

const truncateFileName = (fileName, maxLength = 50) => {
  if (fileName.length <= maxLength) return fileName;
  return fileName.substring(0, maxLength) + '...';
};

interface FileItem {
  name: string;
  displayName?: string;
  type: "file";
  hash?: string;
  collection?: string;
  onDelete?: () => void;
  onDownload?: () => void;
}

interface FolderItem {
  name: string;
  displayName?: string;
  type: "folder";
  onClick?: () => void;
}

type ListItem = FileItem | FolderItem;

interface FileListProps {
  items: ListItem[];
  onDelete?: (name: string) => void;
  viewMode: "list" | "grid";
}

export function FileList({ items, onDelete, viewMode }: FileListProps) {
  const { isDarkMode } = useTheme();
  const [config, setConfig] = useState({ DEFAULT_COLLECTION: "public" });

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch("/configuration.json");
        const data = await response.json();
        setConfig(data);
      } catch (error) {
        console.error("Error loading config:", error);
      }
    };
    fetchConfig();
  }, []);

  const handleDelete = (e: React.MouseEvent, item: ListItem) => {
    e.stopPropagation();
    
    if (item.type === "file") {
      if (item.onDelete) {
        item.onDelete();
      }
    } 
  };

  const handleDownload = (e: React.MouseEvent, item: ListItem) => {
    e.stopPropagation();
    
    if (item.type === "file" && item.onDownload) {
      item.onDownload();
    }
  };

  const processedItems = items.map(item => ({
    ...item,
    displayName: item.displayName || truncateFileName(item.name)
  }));

  if (viewMode === "list") {
    return (
      <Box>
        {processedItems.map((item, index) => (
          <Box
            key={`${item.name}-${index}`}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            p={1}
            onClick={item.type === "folder" && item.onClick ? item.onClick : undefined}
            sx={{
              borderBottom: isDarkMode
                ? "1px solid #3f3f46"
                : "1px solid #e5e7eb",
              cursor: item.type === "folder" && item.onClick ? "pointer" : "default",
              "&:hover": { bgcolor: isDarkMode ? "#27272a" : "#f3f4f6" },
              transition: "background-color 0.2s",
            }}
          >
            <Box display="flex" alignItems="center" gap={1} overflow="hidden" maxWidth="calc(100% - 30px)">
              {item.type === "folder" ? (
                <Folder
                  size={20}
                  className={isDarkMode ? "text-green-500" : "text-green-600"}
                />
              ) : (
                <File
                  size={20}
                  className={isDarkMode ? "text-blue-500" : "text-blue-600"}
                />
              )}
              <Tooltip title={item.name} placement="top">
                <Typography 
                  color={isDarkMode ? "white" : "text-gray-900"}
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap"
                  }}
                >
                  {item.displayName}
                </Typography>
              </Tooltip>
              {item.type === "folder" && item.name === config.DEFAULT_COLLECTION && (
                <Lock 
                  size={14} 
                  className={isDarkMode ? "text-gray-400" : "text-gray-600"} 
                />
              )}
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              {item.type === "file" && item.onDownload && (
                <Tooltip title="Abrir archivo" placement="top">
                  <Download
                    size={16}
                    className={`cursor-pointer ${isDarkMode
                        ? "text-blue-500 hover:text-blue-600"
                        : "text-blue-600 hover:text-blue-500"
                      }`}
                    onClick={(e) => handleDownload(e, item)}
                  />
                </Tooltip>
              )}
              {item.type === "file" && item.onDelete && (
                <Tooltip title="Eliminar" placement="top">
                  <Trash
                    size={16}
                    className={`cursor-pointer ${isDarkMode
                        ? "text-red-500 hover:text-red-600"
                        : "text-red-600 hover:text-red-500"
                      }`}
                    onClick={(e) => handleDelete(e, item)}
                  />
                </Tooltip>
              )}
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
        gap: 2,
        p: 1,
      }}
    >
      {processedItems.map((item, index) => (
        <Box
          key={`${item.name}-${index}`}
          onClick={item.type === "folder" && item.onClick ? item.onClick : undefined}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
            cursor: item.type === "folder" && item.onClick ? "pointer" : "default",
            borderRadius: "8px",
            position: "relative",
            height: "100px",
            transition: "background-color 0.2s",
            "&:hover": {
              bgcolor: isDarkMode ? "#27272a" : "#f3f4f6",
              "& .action-icons": {
                opacity: 1,
              },
            },
          }}
        >
          {item.type === "folder" ? (
            <Folder
              size={40}
              className={isDarkMode ? "text-green-500" : "text-green-600"}
            />
          ) : (
            <File
              size={40}
              className={isDarkMode ? "text-blue-500" : "text-blue-600"}
            />
          )}
          <Tooltip title={item.name} placement="top">
            <Typography
              color={isDarkMode ? "white" : "text-gray-900"}
              sx={{
                fontSize: "0.75rem",
                mt: 1,
                textAlign: "center",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                width: "100%",
              }}
            >
              {item.displayName}
            </Typography>
          </Tooltip>
          {item.type === "folder" && item.name === config.DEFAULT_COLLECTION && (
            <Box
              sx={{
                position: "absolute",
                top: 4,
                left: 4,
                backgroundColor: isDarkMode ? "#27272a" : "#f3f4f6",
                borderRadius: "50%",
                width: "20px",
                height: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: isDarkMode
                  ? "0 2px 4px rgba(0,0,0,0.2)"
                  : "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              <Lock 
                size={12} 
                className={isDarkMode ? "text-gray-400" : "text-gray-600"} 
              />
            </Box>
          )}
          <Box
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              opacity: 0,
              transition: "all 0.2s",
              display: "flex",
              gap: 0.5,
            }}
            className="action-icons"
          >
            {item.type === "file" && item.onDownload && (
              <Box
                sx={{
                  backgroundColor: isDarkMode ? "#27272a" : "#f3f4f6",
                  borderRadius: "50%",
                  width: "24px",
                  height: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: isDarkMode
                    ? "0 2px 4px rgba(0,0,0,0.2)"
                    : "0 2px 4px rgba(0,0,0,0.1)",
                }}
              >
                                 <Tooltip title="Abrir archivo" placement="top">
                  <Download
                    size={14}
                    className={`cursor-pointer ${
                      isDarkMode
                        ? "text-blue-500 hover:text-blue-600"
                        : "text-blue-600 hover:text-blue-500"
                    }`}
                    onClick={(e) => handleDownload(e, item)}
                  />
                </Tooltip>
              </Box>
            )}
            {item.type === "file" && item.onDelete && (
              <Box
                sx={{
                  backgroundColor: isDarkMode ? "#27272a" : "#f3f4f6",
                  borderRadius: "50%",
                  width: "24px",
                  height: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: isDarkMode
                    ? "0 2px 4px rgba(0,0,0,0.2)"
                    : "0 2px 4px rgba(0,0,0,0.1)",
                }}
              >
                <Tooltip title="Eliminar" placement="top">
                  <Trash
                    size={14}
                    className={`cursor-pointer ${
                      isDarkMode
                        ? "text-red-500 hover:text-red-600"
                        : "text-red-600 hover:text-red-500"
                    }`}
                    onClick={(e) => handleDelete(e, item)}
                  />
                </Tooltip>
              </Box>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export default FileList;